import type { ComponentProps } from "react"
import { useState } from "react"
import Input from "@/v2-ui/input"
import Label from "@/v2-ui/label"
import Switch from "@/v2-ui/switch"
import Textarea from "@/v2-ui/textarea"
import { errorToast, toast } from "@/v2-ui/toast"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import FeatureDetailsTexts from "@/v2-backoffice-client/feature/details/FeatureDetailsTexts"
import {
  useLayerSublayerDeleteMutation,
  useLayerSublayerUpsertMutation,
} from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.mutation"
import type { StudioSublayer } from "@/v2-backoffice-client/layer/sublayer/layer.sublayer.types"

export type LayerSublayerEditorProps = ComponentProps<"div"> & {
  parentFeatureCode: string
  environment: string
  /** Absent in the "add a sublayer" row. */
  sublayer?: StudioSublayer
}

function parseConfiguration(text: string): Record<string, unknown> | null {
  const trimmed = text.trim()
  if (!trimmed) return null
  const parsed: unknown = JSON.parse(trimmed)
  if (typeof parsed !== "object" || parsed === null || Array.isArray(parsed)) {
    throw new Error("Configuration must be a JSON object")
  }
  return parsed as Record<string, unknown>
}

/**
 * One sublayer row: Key (fixed once created, since the row's feature code is
 * '<parent>/<Key>'), rank, enabled, the sublayer's own Configuration blob
 * (prefilled from the catalog when the export carries it; an empty save keeps
 * what is stored), and its texts.
 *
 * Texts: a saved sublayer owns a common.Features row, so it gets the same
 * editor as the layer itself (FeatureDetailsTexts, every locale, Claude fill),
 * and Save here sends Name/Description as null, which the proc reads as
 * "keep". The add row is the exception: no feature row exists yet, so it
 * takes an en-GB Name/Description to create with; other locales follow in the
 * editor that appears once the row is saved.
 */
function LayerSublayerEditor(props: LayerSublayerEditorProps) {
  const { parentFeatureCode, environment, sublayer, className, ...divProps } =
    props
  const isNew = !sublayer

  const [key, setKey] = useState(sublayer?.Key ?? "")
  // Add row only; saved rows edit texts through FeatureDetailsTexts below.
  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [priority, setPriority] = useState(String(sublayer?.Priority ?? 0))
  const [isEnabled, setIsEnabled] = useState(sublayer?.IsEnabled ?? true)
  const [configuration, setConfiguration] = useState(
    sublayer?.Configuration
      ? JSON.stringify(sublayer.Configuration, null, 2)
      : "",
  )
  const [configurationError, setConfigurationError] = useState<
    Error | undefined
  >()

  const [upsert, upsertState] = useLayerSublayerUpsertMutation()
  const [remove, removeState] = useLayerSublayerDeleteMutation()
  errorToast(upsertState.error, { title: "Failed to save the sublayer" })
  errorToast(removeState.error, { title: "Failed to remove the sublayer" })
  errorToast(configurationError, { title: "Configuration is not valid JSON" })

  const fieldId = `layer-sublayer-${sublayer?.Key ?? "new"}`

  async function onSave() {
    let parsedConfiguration: Record<string, unknown> | null
    try {
      parsedConfiguration = parseConfiguration(configuration)
    } catch (error) {
      setConfigurationError(error as Error)
      return
    }
    setConfigurationError(undefined)

    const result = await upsert({
      variables: {
        input: {
          ParentFeatureCode: parentFeatureCode,
          Environment: environment,
          Key: key.trim(),
          Name: isNew ? name.trim() || null : null,
          Description: isNew ? description.trim() || null : null,
          Priority: Number(priority) || 0,
          IsEnabled: isEnabled,
          Configuration: parsedConfiguration,
        },
      },
    })
    if (!result.data) return
    toast({
      title: isNew
        ? "Sublayer added to the catalog."
        : "Sublayer saved to the catalog.",
    })
    if (isNew) {
      setKey("")
      setName("")
      setDescription("")
      setPriority("0")
      setConfiguration("")
    }
  }

  async function onRemove() {
    if (!sublayer) return
    const result = await remove({
      variables: {
        input: {
          ParentFeatureCode: parentFeatureCode,
          Environment: environment,
          Key: sublayer.Key,
        },
      },
    })
    if (result.data) toast({ title: "Sublayer disabled in the catalog." })
  }

  return (
    <div
      className={cn(
        "layer-sublayer-editor flex flex-col gap-2 rounded-md border p-3",
        !isNew && !sublayer.IsEnabled && "opacity-70",
        className,
      )}
      {...divProps}
    >
      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${fieldId}-key`}>Key</Label>
          <Input
            id={`${fieldId}-key`}
            className="w-56 font-mono text-xs"
            placeholder="fiber_punkt_1000"
            value={key}
            disabled={!isNew}
            onChange={(event) => setKey(event.target.value)}
          />
        </div>
        {isNew && (
          <div className="flex flex-col gap-1">
            <Label htmlFor={`${fieldId}-name`}>Name (en-GB)</Label>
            <Input
              id={`${fieldId}-name`}
              className="w-64"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </div>
        )}
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${fieldId}-priority`}>Rank</Label>
          <Input
            id={`${fieldId}-priority`}
            className="w-20"
            type="number"
            inputMode="numeric"
            value={priority}
            onChange={(event) => setPriority(event.target.value)}
          />
        </div>
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${fieldId}-enabled`}>Enabled</Label>
          <Switch
            id={`${fieldId}-enabled`}
            size="xs"
            checked={isEnabled}
            onCheckedChange={setIsEnabled}
          />
        </div>
      </div>

      {isNew ? (
        <div className="flex flex-col gap-1">
          <Label htmlFor={`${fieldId}-description`}>Description (en-GB)</Label>
          <Input
            id={`${fieldId}-description`}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
        </div>
      ) : (
        <FeatureDetailsTexts
          compact
          featureCode={`${parentFeatureCode}/${sublayer.Key}`}
          subject="sublayer"
          featureCodeLabel="Layers_Sublayer"
        />
      )}

      <div className="flex flex-col gap-1">
        <Label htmlFor={`${fieldId}-configuration`}>
          Configuration JSON{isNew ? "" : " (empty keeps the stored one)"}
        </Label>
        <Textarea
          id={`${fieldId}-configuration`}
          rows={3}
          className="font-mono text-xs"
          placeholder='{"legendUrl": "...", "minScale": 250000, "maxScale": 0, "visible": true}'
          value={configuration}
          onChange={(event) => setConfiguration(event.target.value)}
        />
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          variant="secondary"
          isLoading={upsertState.loading}
          disabled={!key.trim()}
          data-feature-code={
            isNew
              ? "UI_BackofficeLayers_Sublayers_Add"
              : "UI_BackofficeLayers_Sublayers_Save"
          }
          onClick={() => void onSave()}
        >
          {isNew ? "Add sublayer" : "Save"}
        </Button>
        {!isNew && sublayer.IsEnabled && (
          <Button
            size="sm"
            variant="ghost"
            isLoading={removeState.loading}
            data-feature-code="UI_BackofficeLayers_Sublayers_Remove"
            onClick={() => void onRemove()}
          >
            Remove
          </Button>
        )}
      </div>
    </div>
  )
}

export default LayerSublayerEditor
